import L from "leaflet";
import { useEffect, useRef } from "react";
import { colorForDesign, type Poster } from "../lib/types";

interface PosterMapProps {
  posters: Poster[];
  selectedPoster?: Poster;
  onSelect: (poster: Poster) => void;
}

export function PosterMap({ posters, selectedPoster, onSelect }: PosterMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    mapRef.current = L.map(containerRef.current, { zoomControl: false }).setView([39.7684, -86.1581], 12);
    L.control.zoom({ position: "bottomright" }).addTo(mapRef.current);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap"
    }).addTo(mapRef.current);
    layerRef.current = L.layerGroup().addTo(mapRef.current);
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    const placed = posters.filter((poster) => poster.isPlaced && poster.placementLat && poster.placementLng);
    placed.forEach((poster) => {
      const color = colorForDesign(poster.designType);
      const marker = L.circleMarker([poster.placementLat!, poster.placementLng!], {
        radius: poster.posterId === selectedPoster?.posterId ? 12 : 9,
        color,
        fillColor: color,
        fillOpacity: 0.85,
        weight: 3
      });
      marker.bindTooltip(`${poster.posterId} · ${poster.scanCount} scans`);
      marker.on("click", () => onSelect(poster));
      marker.addTo(layer);
    });

    if (placed.length > 0) {
      const bounds = L.latLngBounds(placed.map((poster) => [poster.placementLat!, poster.placementLng!]));
      map.fitBounds(bounds.pad(0.2), { maxZoom: 14 });
    }
  }, [posters, selectedPoster, onSelect]);

  return <div className="poster-map" ref={containerRef} />;
}
